import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showErrorThunk, showSuccessThunk } from '@spike/notifications-action';
import Button from 'components/UI/V3/Button/Button';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { useDrawerContext } from 'components/UI/V3/Drawer/DrawerContext';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { Select, SelectOption } from 'components/UI/V3/Forms/Select/Select';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal/ExitWithoutSavingModal';
import { useAuth } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchIdogcamRooms, updateIdogcamCamera } from '../api/IdogcamApi';
import IdogcamCameraTypeCard from '../IdogcamCameraTypeCard/IdogcamCameraTypeCard';
import {
    createCameraForm,
    IdogcamCameraFormErrors,
    IdogcamCameraFormField,
    isCameraFormDirty,
    validateCameraForm
} from '../model/IdogcamCameraForm';
import {
    IdogcamAccessType,
    IdogcamCamera,
    IdogcamCameraChanges,
    IdogcamLocation,
    IdogcamRoom
} from '../model/IdogcamModel';
import styles from './IdogcamCameraDrawer.module.scss';

interface IdogcamCameraDrawerProps {
    camera: IdogcamCamera;
    marketplaceId: number;
    locations: Array<IdogcamLocation>;
    onClose: () => void;
    onSaved: (camera: IdogcamCamera) => void;
}

const BACKEND_FIELDS: Record<string, IdogcamCameraFormField> = {
    access_type: 'accessType',
    address_id: 'addressId',
    room_id: 'roomId',
    room_unit_id: 'roomUnitId'
};

// Maps the backend 422 payload ({ errors: { address_id: ["is required ..."] } }) to form fields.
const extractServerErrors = (error: any): { fields: IdogcamCameraFormErrors; messages: Array<string> } => {
    const errors = error?.response?.data?.errors ?? {};
    const fields: IdogcamCameraFormErrors = {};
    const messages: Array<string> = [];

    Object.entries(errors).forEach(([key, value]) => {
        const message = Array.isArray(value) ? String(value[0]) : String(value);
        const field = BACKEND_FIELDS[key];
        if (field) {
            fields[field] = message.charAt(0).toUpperCase() + message.slice(1);
        } else {
            messages.push(`${key} ${message}`);
        }
    });

    return { fields, messages };
};

const DrawerTitle: FunctionComponent<{ camera: IdogcamCamera; locationName?: string }> = ({ camera, locationName }) => {
    const { close } = useDrawerContext();

    return (
        <div className={styles.header}>
            <div className={styles.headerTexts}>
                <p className={styles.title}>{camera.name}</p>
                <p className={styles.subtitle}>
                    Camera ID: {camera.externalId}
                    {locationName && (
                        <>
                            {' · '}
                            <FontAwesomeIcon icon={faLocationDot} />
                            {locationName}
                        </>
                    )}
                </p>
            </div>
            <button
                type="button"
                aria-label="Close"
                className={styles.closeButton}
                onClick={close}
            >
                <FontAwesomeIcon icon={faXmark} />
            </button>
        </div>
    );
};

export const IdogcamCameraDrawer: FunctionComponent<IdogcamCameraDrawerProps> = props => {
    const auth = useAuth();
    const dispatch = useDispatch();

    const initialForm = useMemo(() => createCameraForm(props.camera, props.locations), [props.camera, props.locations]);

    const [form, setForm] = useState<IdogcamCameraChanges>(initialForm);
    const [serverErrors, setServerErrors] = useState<IdogcamCameraFormErrors>({});
    const [rooms, setRooms] = useState<Array<IdogcamRoom>>([]);
    const [loadingRooms, setLoadingRooms] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchIdogcamRooms(props.marketplaceId, auth.token!)
            .then(result => !cancelled && setRooms(result))
            .catch(() => !cancelled && dispatch(showErrorThunk("We couldn't load the rooms. Please try again.")))
            .finally(() => !cancelled && setLoadingRooms(false));
        return () => {
            cancelled = true;
        };
    }, [props.marketplaceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const errors = validateCameraForm(form);
    const isDirty = isCameraFormDirty(form, initialForm);
    const canSave = isDirty && Object.keys(errors).length === 0 && !saving;

    const locationOptions: Array<SelectOption<IdogcamLocation>> = props.locations.map(location => ({
        label: location.name,
        value: String(location.id),
        data: location
    }));

    const roomOptions: Array<SelectOption<IdogcamRoom>> = rooms.map(room => ({
        label: room.name,
        value: String(room.id),
        data: room
    }));

    const selectedRoom = rooms.find(room => room.id === form.roomId);
    // Keeps an already-mapped unit visible even if it was deactivated later.
    const unitOptions: Array<SelectOption<number>> = (selectedRoom?.units ?? [])
        .filter(unit => unit.active || unit.id === form.roomUnitId)
        .map(unit => ({ label: unit.name, value: String(unit.id), data: unit.id }));

    const locationName =
        props.locations.find(location => location.id === form.addressId)?.name ?? props.camera.addressName ?? undefined;

    const updateForm = (changes: Partial<IdogcamCameraChanges>) => {
        setForm(prev => ({ ...prev, ...changes }));
        setServerErrors({});
    };

    const changeAccessTypeHandler = (accessType: IdogcamAccessType) => {
        updateForm({ accessType });
    };

    const changeRoomHandler = (value: string) => {
        const roomId = Number(value);
        // A unit always belongs to its room; changing room clears the unit.
        updateForm({ roomId, roomUnitId: roomId === form.roomId ? form.roomUnitId : null });
    };

    const saveHandler = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const saved = await updateIdogcamCamera(props.marketplaceId, auth.token!, props.camera.id, form);
            dispatch(showSuccessThunk('Camera saved successfully'));
            props.onSaved(saved);
        } catch (error) {
            const { fields, messages } = extractServerErrors(error);
            setServerErrors(fields);
            if (Object.keys(fields).length === 0) {
                dispatch(showErrorThunk(messages[0] ?? "We couldn't save the camera. Please try again."));
            }
            setSaving(false);
        }
    };

    const beforeCloseHandler = (): boolean => {
        if (isDirty && !saving) {
            setShowExitModal(true);
            return false;
        }
        return true;
    };

    return (
        <>
            <Drawer
                onBeforeClose={beforeCloseHandler}
                onClose={props.onClose}
            >
                <DrawerTitle
                    camera={props.camera}
                    locationName={locationName}
                />
                <DrawerBody className={styles.body}>
                    <section className={styles.section}>
                        <Label>Status</Label>
                        <div className={styles.statusRow}>
                            <Switch
                                checked={form.active}
                                onChange={active => updateForm({ active })}
                            />
                            <span>{form.active ? 'Active' : 'Inactive'}</span>
                        </div>
                    </section>

                    <section className={styles.section}>
                        {/* Client-side rules only gate the Save button; inline errors come from the backend. */}
                        <Label error={serverErrors.accessType}>Camera Type</Label>
                        <div
                            role="radiogroup"
                            className={styles.typeCards}
                        >
                            <IdogcamCameraTypeCard
                                icon={faEye}
                                title="Public"
                                description="Available to all pet parents during their scheduled appointments."
                                selected={form.accessType === 'public'}
                                onSelect={() => changeAccessTypeHandler('public')}
                            />
                            <IdogcamCameraTypeCard
                                icon={faEyeSlash}
                                title="Private"
                                description="Visible only to pet parents with a pet currently in a room."
                                selected={form.accessType === 'private'}
                                onSelect={() => changeAccessTypeHandler('private')}
                            />
                        </div>
                    </section>

                    {form.accessType && (
                        <section className={styles.section}>
                            <Select
                                label="Location"
                                required={true}
                                placeholder="Select a location"
                                options={locationOptions}
                                value={form.addressId ? String(form.addressId) : undefined}
                                error={serverErrors.addressId}
                                onChange={value => updateForm({ addressId: Number(value) })}
                            />
                        </section>
                    )}

                    {form.accessType === 'private' && (
                        <section className={styles.roomUnitRow}>
                            <Select
                                label="Room"
                                required={true}
                                placeholder={loadingRooms ? 'Loading rooms...' : 'Select a room'}
                                options={roomOptions}
                                value={form.roomId ? String(form.roomId) : undefined}
                                disabled={loadingRooms}
                                error={serverErrors.roomId}
                                className={styles.halfSelect}
                                onChange={changeRoomHandler}
                            />
                            <Select
                                label="Unit"
                                required={true}
                                placeholder="Select a unit"
                                options={unitOptions}
                                value={form.roomUnitId ? String(form.roomUnitId) : undefined}
                                disabled={!selectedRoom}
                                error={serverErrors.roomUnitId}
                                className={styles.halfSelect}
                                onChange={value => updateForm({ roomUnitId: Number(value) })}
                            />
                        </section>
                    )}
                </DrawerBody>
                <DrawerFooter>
                    <Button
                        label="Save"
                        variant="green"
                        size="large"
                        fullWidth={true}
                        disabled={!canSave}
                        isLoading={saving}
                        onClick={saveHandler}
                    />
                </DrawerFooter>
            </Drawer>

            {showExitModal && (
                <ExitWithoutSavingModal
                    onDiscard={() => {
                        setShowExitModal(false);
                        props.onClose();
                    }}
                    onKeepEditing={() => setShowExitModal(false)}
                />
            )}
        </>
    );
};

export default IdogcamCameraDrawer;
